import { COLOR } from 'app-v2/design-tokens'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'
import { media } from 'app/assets/styles'
import { Recaptcha } from 'app/common/recaptcha'
import { H3, H4 } from 'app/common/typography'
import { Layout } from 'app/ecommerce/common'
import { AuthenticationDuringOrder } from 'app/ecommerce/common/components/authenticationDuringOrder'
import {
    WidgetCollectMode,
    WidgetEditRestaurant
} from 'app/ecommerce/common/components/delivery-choice'
import React, { Component } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import burger_choose_resto from 'shared/assets/images/burger_choose_resto.png'
import { colors as oldColors } from 'shared/assets/styles'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import type { CouponForm, MetaShortDTO, RestaurantShortDTO } from 'shared/types'
import { orderPickUpType, restaurantServices } from 'shared/utils'
import styled from 'styled-components'

const { TAKEAWAY, ONSITE, TABLESERVICE, DRIVE } = orderPickUpType
const { tertiary } = oldColors

const ICON_SIZE = 36

type Props = {
    navigation: any,
    intl: IntlShape,
    restaurantPageUrl?: string,
    products: Array<PurchasableItem>,
    restaurant: RestaurantShortDTO,
    setCollectMode: (collectMode: string) => void,
    cadAvailable: boolean,
    cagAvailable: boolean,
    casAvailable: boolean,
    fetchRestaurant: (pathUrl: string) => void,
    pageMetadata: (payload: MetaShortDTO) => void,
    navigate: (to: string) => void,
    addCoupon: CouponForm => void,
    removeProductFromCart: (product: PurchasableItem) => void,
    authenticated: boolean
}

type State = {
    showCaptcha: boolean,
    showAuthent: boolean
}

class _DeliveryChoiceComponent extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    state = {
        showCaptcha: false,
        showAuthent: false
    }

    async componentDidMount() {
        const {
            pageMetadata,
            restaurant,
            restaurantPageUrl,
            fetchRestaurant,
            navigate,
            intl
        } = this.props
        let restaurantIsSet = !!restaurant
        if (restaurantPageUrl) {
            restaurantIsSet = await fetchRestaurant(restaurantPageUrl)
        }
        if (!restaurantIsSet) {
            return navigate(
                intl.formatMessage({
                    id: 'routes.clickandcollect.restaurant.choice'
                })
            )
        }
        pageMetadata({})
        this.initRecaptchaPromise()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    checkImpersonalOperationProduct = async (
        item: PurchasableItem,
        collectChoice: string,
        restaurantId: string
    ) => {
        const { addCoupon, removeProductFromCart } = this.props
        if (item.isImpersonalOperation) {
            const code =
                item.promotions &&
                item.promotions[0] &&
                item.promotions[0].couponCode
            if (code) {
                try {
                    this.setState({ showCaptcha: true })
                    const recaptchaToken = await this.recaptchaPromise
                    const form = {
                        code,
                        pickUpType: collectChoice,
                        restaurant: restaurantId,
                        queen: recaptchaToken
                    }
                    const coupon = await addCoupon(form)
                    if (!coupon) {
                        removeProductFromCart(item)
                    }
                } finally {
                    this.setState({ showCaptcha: false })
                    this.initRecaptchaPromise()
                }
            }
        }
    }

    handleClick = async (collectChoice: string) => {
        const {
            products,
            restaurant,
            setCollectMode,
            intl,
            navigate,
            authenticated
        } = this.props
        const catalogRoute = intl.formatMessage({
            id: 'routes.clickandcollect.catalog'
        })
        const restaurantId = restaurant && restaurant.id
        products &&
            products.forEach(item =>
                this.checkImpersonalOperationProduct(
                    item,
                    collectChoice,
                    restaurantId
                )
            )
        await setCollectMode(collectChoice)

        authenticated
            ? navigate(catalogRoute)
            : this.setState({ showAuthent: true })
    }

    renderRestaurantSelected = () => {
        const { restaurant, intl } = this.props
        return (
            <WidgetRestaurantContainer>
                <WidgetEditRestaurant
                    restaurant={restaurant}
                    url={intl.formatMessage({
                        id: 'routes.clickandcollect.restaurant.choice'
                    })}
                />
            </WidgetRestaurantContainer>
        )
    }

    renderWidgetTitle = (titleKey: string) => {
        return (
            <TitleContainer>
                <FormattedMessage id={titleKey}>
                    {(text: string) => <H4>{text}</H4>}
                </FormattedMessage>
            </TitleContainer>
        )
    }

    renderOrderChoice = () => {
        const {
            intl,
            cadAvailable,
            cagAvailable,
            casAvailable,
            restaurant
        } = this.props
        const { showCaptcha } = this.state
        const collectChoiceOnSite =
            restaurant &&
            restaurant.services.includes(restaurantServices.SERVICE_TABLE)
                ? TABLESERVICE
                : ONSITE
        const catalogRoute = intl.formatMessage({
            id: 'routes.clickandcollect.catalog'
        })

        return (
            <div>
                <FormattedMessage id="clickandcollect.delivery.choice.subtitle">
                    {(label: string) => <StyledH3>{label}</StyledH3>}
                </FormattedMessage>

                {cagAvailable && (
                    <WidgetCollectMode
                        title={this.renderWidgetTitle(
                            'clickandcollect.delivery.choice.takeaway'
                        )}
                        description={intl.formatMessage({
                            id:
                                'clickandcollect.delivery.choice.takeaway.description'
                        })}
                        collectChoice={TAKEAWAY}
                        handleClick={this.handleClick}
                        iconCode={tokens.takeaway}
                        iconColor={COLOR.PRIMARY}
                        iconSize={ICON_SIZE}
                        url={catalogRoute}
                    />
                )}

                {casAvailable && (
                    <WidgetCollectMode
                        title={this.renderWidgetTitle(
                            'clickandcollect.delivery.choice.onsite'
                        )}
                        description={intl.formatMessage({
                            id:
                                'clickandcollect.delivery.choice.onsite.description'
                        })}
                        collectChoice={collectChoiceOnSite}
                        handleClick={this.handleClick}
                        iconCode={tokens.onSite}
                        iconColor={COLOR.PRIMARY}
                        iconSize={ICON_SIZE}
                        url={catalogRoute}
                    />
                )}

                {cadAvailable && (
                    <WidgetCollectMode
                        title={this.renderWidgetTitle(
                            'clickandcollect.delivery.choice.drive'
                        )}
                        description={intl.formatMessage({
                            id:
                                'clickandcollect.delivery.choice.drive.description'
                        })}
                        collectChoice={DRIVE}
                        iconCode={tokens.drive}
                        iconColor={COLOR.PRIMARY}
                        iconSize={ICON_SIZE}
                        handleClick={this.handleClick}
                        url={catalogRoute}
                    />
                )}

                {showCaptcha && (
                    <Recaptcha
                        action="WebCCDeliveryChoice"
                        verifyCallback={this.verifyCallback}
                    />
                )}
            </div>
        )
    }

    renderImageBurger = () => {
        return (
            <ImageContainer>
                <ImageBurger src={burger_choose_resto} height="100%" />
            </ImageContainer>
        )
    }

    render() {
        const { showAuthent } = this.state
        return (
            <Layout
                background="plain"
                border
                closeEnabled
                noDelivery
                backgroundColor={tertiary}>
                <StyledResponsiveContainer>
                    {this.renderRestaurantSelected()}
                    {this.renderOrderChoice()}
                    {this.renderImageBurger()}
                </StyledResponsiveContainer>
                <AuthenticationDuringOrder
                    comesFrom="C&C"
                    visible={showAuthent}
                    setVisible={visible =>
                        this.setState({ showAuthent: visible })
                    }
                />
            </Layout>
        )
    }
}

const StyledResponsiveContainer = styled.div`
    margin: 90px 5px;
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 1200px;
    min-height: calc(100vh - 180px);
    ${media.medium`
        margin-top: 150px;
        min-height: calc(100vh - 240px);
    `};
`

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 30px;
`

const ImageBurger = styled.img`
    width: 30%;
    ${media.small`
        width: 25%;
    `};
    ${media.medium`
        width: 20%;
    `}
    height: 100%;
`

const StyledH3 = styled(H3)`
    text-align: center;
    margin: 15px;
`

const WidgetRestaurantContainer = styled.div`
    display: flex;
    margin-top: 10px;
    justify-content: space-between;
`

const TitleContainer = styled.div`
    display: flex;
`

export const DeliveryChoiceComponent = injectIntl(_DeliveryChoiceComponent)
